import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { devicesActions, staffActions } from '@/store';
import { selectPOIs } from '@/store/selectors';
import { useTranslation } from 'react-i18next';
import { Globe, Check, ArrowLeftRight, Activity, Settings2, History, Loader2, X, Waypoints, LocateFixed, Magnet, Car, Users, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import NotificationBell from '@/components/layout/NotificationBell';
import MapView from '@/components/map/MapView';
import VehicleListPanel from './VehicleListPanel';
import TelemetryPanel from './TelemetryPanel';
import useElementHeight from '@/hooks/useElementHeight';
import MiniReplayLayer from './MiniReplayLayer';
import ReplayEventLayer from './ReplayEventLayer';
import ReplayTripPlayer from './ReplayTripPlayer';
import { useReplayEvents } from '@/hooks/useReplayEvents';
import MapContextMenu from '@/components/shared/MapContextMenu';
import POIContextMenu from '@/components/shared/POIContextMenu';
import POIForm from '@/features/pois/POIForm';
import VisitHistoryDrawer from '@/features/pois/VisitHistoryDrawer';
import useResizablePanel from '@/hooks/useResizablePanel';
import useTrails from '@/hooks/useTrails';
import { useLiveMapAnimate } from '@/hooks/useAnimatedPositions';
import { useLiveMapFollow } from '@/hooks/useLiveMapFollow';
import { useLiveMapSnap, useLiveMapSnapThreshold, useSnapCounts } from '@/hooks/useSnappedTrails';
import useFeatures from '@/hooks/useFeatures';
import useTelemetryPrefs from '@/hooks/useTelemetryPrefs';
import { useStaffPositions } from '@/hooks/useStaffPositions';
import { useReplayEngine } from '@/features/replay/useReplayEngine';
import RiskmizeMapOverlay from '@/features/riskmize/RiskmizeMapOverlay';
import AircraftOverlay from '@/features/map/layers/AircraftOverlay';
import LiveuamapOverlay from '@/features/map/layers/LiveuamapOverlay';
import { copyCoordsToClipboard } from '@/lib/coords';
import { layers as layersApi } from '@/lib/trackizy';
import api from '@/lib/api';
import { ColdChainSharedProvider } from '@/features/cold-chain/ColdChainShared';
import maplibregl from 'maplibre-gl';
import dayjs from 'dayjs';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'ar', label: 'العربية', flag: 'ع' },
  { code: 'ckb', label: 'کوردی', flag: 'کو' },
];

// Vertical drag handle on the side telemetry drawer's map-facing edge.
// Same stale-closure guard as DockResizeHandle: capture start X + start
// width at mousedown, compute the absolute width from the cumulative delta.
// `drawerSide` is the drawer's logical side; dragging toward the map
// (away from the drawer's own edge) makes it wider, in LTR and RTL.
// Like useResizablePanel, the drag is also capped at a share of the
// viewport so a wide drawer can't push the map controls into the list's
// Flip/Trails stack on a narrow screen (the pref clamp alone is 640).
const SIDE_DRAWER_VIEWPORT_SHARE = 0.3;
function SideDrawerResizeHandle({ value, onChange, drawerSide, ariaLabel }) {
  const dragRef = useRef({ startX: 0, startValue: 0 });
  const handleDown = (e) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startValue: value };
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    const isRTL = document.documentElement.dir === 'rtl';
    const viewportMax = Math.round(window.innerWidth * SIDE_DRAWER_VIEWPORT_SHARE);
    const onMove = (ev) => {
      let delta = ev.clientX - dragRef.current.startX;
      if (isRTL) delta = -delta;
      if (drawerSide === 'end') delta = -delta;
      onChange(Math.min(viewportMax, dragRef.current.startValue + delta));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  return (
    <div
      onMouseDown={handleDown}
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      className="absolute top-0 bottom-0 z-10 flex items-center justify-center group"
      style={{
        width: 8,
        cursor: 'ew-resize',
        [drawerSide === 'end' ? 'insetInlineStart' : 'insetInlineEnd']: -4,
      }}
    >
      <span
        className="flex flex-col items-center gap-0.5 opacity-40 group-hover:opacity-80 transition-opacity"
        aria-hidden="true"
      >
        <span style={{ width: 3, height: 3, borderRadius: '50%', background: '#64748b' }} />
        <span style={{ width: 3, height: 3, borderRadius: '50%', background: '#64748b' }} />
        <span style={{ width: 3, height: 3, borderRadius: '50%', background: '#64748b' }} />
      </span>
    </div>
  );
}

function DockedTelemetrySlot({ t }) {
  const selectedVehicleId = useSelector((s) => s.devices.selectedId);
  const selectedStaffId = useSelector((s) => s.staff.selectedUserId);

  // Violet section header mirroring the green Vehicles / blue People
  // header strips so the docked telemetry reads as a peer section
  // (not an unmarked appendage). The slot itself is pinned to the
  // panel bottom by VehicleListPanel's split layout, so the header
  // is always visible.
  const sectionHeader = (
    <div
      className="flex items-center gap-2 px-2 py-1.5"
      style={{
        background: 'rgba(139, 92, 246, 0.08)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid var(--glass-border)',
      }}
    >
      <Activity size={12} className="text-violet-600" />
      <span className="text-[10px] font-bold text-violet-700 uppercase tracking-wider">
        {t('telemetry.section', 'Telemetry')}
      </span>
    </div>
  );

  if (!selectedVehicleId && !selectedStaffId) {
    return (
      <div>
        {sectionHeader}
        <div className="px-3 py-4 text-center text-[11px] text-slate-400">
          {t('telemetry.dockEmpty', 'Select a vehicle or staff member')}
        </div>
      </div>
    );
  }
  return (
    <div>
      {sectionHeader}
      <TelemetryPanel mode="docked" />
    </div>
  );
}

// One cold-chain overview subscription for the whole page (list chips + telemetry card).
export default function LiveMapPage() {
  return (
    <ColdChainSharedProvider>
      <LiveMapPageInner />
    </ColdChainSharedProvider>
  );
}

function LiveMapPageInner() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  // Asset counts for the top-bar pill. Sourced from Redux directly
  // (not Traccar's online/offline TCP state, which is misleading — a
  // tracker can be "online" with stale GPS or "offline" with a fresh
  // fix). Filters out synthetic staff trackers the same way the panel
  // and `/api/devices` do.
  const devicesById = useSelector((s) => s.devices.items);
  const staffByUserId = useSelector((s) => s.staff.byUserId);
  const vehicleCount = useMemo(
    () => Object.values(devicesById).filter((d) => !d.hidden_from_default_list).length,
    [devicesById],
  );
  const staffCount = useMemo(() => Object.keys(staffByUserId).length, [staffByUserId]);
  const peopleRowVisible = staffCount > 0;
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const mapViewRef = useRef(null);
  const [layersStatus, setLayersStatus] = useState(null);
  useEffect(() => {
    layersApi.status().then(setLayersStatus).catch(() => setLayersStatus(null));
  }, []);
  const panel = useResizablePanel('liveMap', { defaultWidth: 380 });
  const { enabled: trailsEnabled, toggleTrails, trailGeoJSON, settings: trailSettings, updateSettings: updateTrailSettings } = useTrails();
  const [trailSettingsOpen, setTrailSettingsOpen] = useState(false);
  const [animateEnabled, toggleAnimate] = useLiveMapAnimate();
  const [followEnabled, toggleFollow] = useLiveMapFollow();
  const [snapEnabled, toggleSnap] = useLiveMapSnap();
  const [snapThresholdM, setSnapThresholdM, snapThresholdBounds] = useLiveMapSnapThreshold();
  const snapCounts = useSnapCounts(snapThresholdM);
  const [snapSettingsOpen, setSnapSettingsOpen] = useState(false);
  const features = useFeatures();
  const telemetryPrefs = useTelemetryPrefs();
  const [floatingPanelRef, floatingPanelHeight] = useElementHeight();
  // Side-drawer telemetry lives on the map edge opposite the list, the same
  // slot the POI editor, Visit History and Riskmize drawers use — it yields
  // to any of them so two drawers never stack on one edge.
  const sideSelectedVehicleId = useSelector((s) => s.devices.selectedId);
  const sideSelectedStaffId = useSelector((s) => s.staff.selectedUserId);
  const riskDrawerOpen = useSelector((s) => s.riskmize?.drawerOpen);

  // Staff phones layer — Phase 3 polish: poll unconditionally so the People
  // row in VehicleListPanel can hide itself when the org has no staff, then
  // gate visibility with `staffLayerVisible` (toggled by the panel's people
  // icon, not a standalone map button anymore).
  const [staffLayerVisible, setStaffLayerVisible] = useState(true);
  // Vehicle category visibility (list + map). Mirrors the people toggle:
  // hides vehicles from the asset list AND filters them out of the unified
  // cluster source so the map shows people-only.
  const [vehiclesEnabled, setVehiclesEnabled] = useState(() => {
    try { const v = localStorage.getItem('trackizy_livemap_vehiclesEnabled'); return v == null ? true : v === 'true'; } catch { return true; }
  });
  useEffect(() => { localStorage.setItem('trackizy_livemap_vehiclesEnabled', String(vehiclesEnabled)); }, [vehiclesEnabled]);
  // Side-effect only — the hook polls /api/staff/positions/latest and
  // dispatches into state.staff.byUserId. MapView reads from Redux directly
  // to build the unified cluster source.
  useStaffPositions({ enabled: true });

  // Filter state
  const [statusFilter, setStatusFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');

  // Mini replay state — all ephemeral, page-scoped
  const [miniReplay, setMiniReplay] = useState(null);
  // Follow mode: keep the map centered on the replay marker during playback.
  const [replayFollow, setReplayFollow] = useState(() => {
    try { return localStorage.getItem('trackizy_livemap_replay_follow') === 'true'; } catch { return false; }
  });
  const toggleReplayFollow = useCallback(() => {
    setReplayFollow((prev) => {
      const next = !prev;
      try { localStorage.setItem('trackizy_livemap_replay_follow', String(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);
  // Shape: { deviceId: string, deviceName: string, positions: Array } | null
  const [replayLoading, setReplayLoading] = useState(false);
  const [replayError, setReplayError] = useState(null);

  // Map right-click state — owned here so MapContextMenu / POIContextMenu can
  // be rendered as portals; vehicle/staff menus live in VehicleListPanel and
  // open via window-event bridge (see below).
  const [mapMenu, setMapMenu] = useState(null);
  // Shape: { kind: 'map', x, y, lngLat } | { kind: 'poi', x, y, poi } | null

  // POI edit/create drawer — when set, a glass panel mounts POIForm.
  // For "Add POI here" we pass { latitude, longitude } so POIForm uses them
  // as initial state but treats it as a create (poi.id is undefined).
  const [editPoi, setEditPoi] = useState(null);

  // VisitHistoryDrawer target.
  const [visitsPoi, setVisitsPoi] = useState(null);

  const poiList = useSelector(selectPOIs);

  // Date range popover state
  const [datePopover, setDatePopover] = useState(null);
  // Shape: { deviceId: string, deviceName: string, anchorEl: HTMLElement } | null
  const [dateFrom, setDateFrom] = useState(() => dayjs().startOf('day').format('YYYY-MM-DDTHH:mm'));
  const [dateTo, setDateTo] = useState(() => dayjs().endOf('day').format('YYYY-MM-DDTHH:mm'));

  const fitRouteRef = useRef(null);
  const datePopoverRef = useRef(null);

  // useReplayEngine — call always (hooks must not be conditional), pass null positions when inactive
  const replayEngine = useReplayEngine(miniReplay?.positions || null);

  const replayEvents = useReplayEvents(miniReplay);

  // The event nearest (at or just before) the current playhead, for highlight.
  const activeEventId = useMemo(() => {
    if (!replayEvents.length) return null;
    const p = replayEngine.progress;
    let best = null;
    for (const e of replayEvents) {
      if (e.progress <= p + 0.0001) best = e;
    }
    return best?.id ?? null;
  }, [replayEvents, replayEngine.progress]);

  // `replayEngine` is a fresh object every render (new {progress,...} literal),
  // so it changes on every RAF tick during playback. Hold it in a ref so
  // handleSeekToEvent stays referentially stable — otherwise ReplayEventLayer's
  // effect (dep on onSelect) would tear down + rebuild every DOM marker at 60fps
  // while the replay plays. Same ref pattern documented in src/CLAUDE.md.
  const replayEngineRef = useRef(replayEngine);
  replayEngineRef.current = replayEngine;

  const handleSeekToEvent = useCallback((evt) => {
    if (!evt) return;
    const engine = replayEngineRef.current;
    engine.seek(evt.progress);
    if (engine.playing) engine.toggle(); // pause on jump
    const map = mapViewRef.current?.getMap?.();
    if (map && evt.lat != null && evt.lng != null) {
      map.flyTo({ center: [evt.lng, evt.lat], zoom: Math.max(map.getZoom(), 15) });
    }
  }, []);

  const handleStartReplay = useCallback((deviceId, deviceName, anchorEl) => {
    setDateFrom(dayjs().startOf('day').format('YYYY-MM-DDTHH:mm'));
    setDateTo(dayjs().endOf('day').format('YYYY-MM-DDTHH:mm'));
    setReplayError(null);
    setDatePopover({ deviceId, deviceName, anchorEl });
  }, []);

  const handleFetchReplay = useCallback(async () => {
    if (!datePopover) return;
    const { deviceId, deviceName } = datePopover;
    setReplayLoading(true);
    setReplayError(null);
    try {
      const positions = await api.getRoute({
        deviceId,
        from: new Date(dateFrom).toISOString(),
        to: new Date(dateTo).toISOString(),
      });
      if (!positions || positions.length < 2) {
        setReplayError('noPositions');
        setReplayLoading(false);
        return;
      }
      setMiniReplay({ deviceId, deviceName, positions });
      setDatePopover(null);
    } catch (err) {
      console.error('Failed to fetch route:', err);
      setReplayError('errorLoading');
    } finally {
      setReplayLoading(false);
    }
  }, [datePopover, dateFrom, dateTo]);

  const handleCloseReplay = useCallback(() => {
    setMiniReplay(null);
    replayEngineRef.current.reset(); // ref: replayEngine is a new object every frame
  }, []);

  const handlePreset = useCallback((presetFrom, presetTo) => {
    setDateFrom(presetFrom().format('YYYY-MM-DDTHH:mm'));
    setDateTo(presetTo().format('YYYY-MM-DDTHH:mm'));
  }, []);

  // Date popover outside-click / Esc handler
  useEffect(() => {
    if (!datePopover) return;
    const handleClick = (e) => {
      if (datePopoverRef.current && !datePopoverRef.current.contains(e.target)) {
        setDatePopover(null);
      }
    };
    const handleEsc = (e) => { if (e.key === 'Escape') setDatePopover(null); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [datePopover]);

  // Replay position for TelemetryPanel
  const replayPosition = miniReplay ? replayEngine.currentPosition : null;

  useEffect(() => {
    if (!langOpen) return;
    const handleClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [langOpen]);

  // Push staff layer visibility + data into MapView via its imperative API.
  useEffect(() => {
    mapViewRef.current?.setStaffVisible?.(staffLayerVisible);
  }, [staffLayerVisible]);

  // Deeplink fly-to. NotificationBell + AlertHistory write to sessionStorage
  // before navigating here AND dispatch a window event (covers "already on
  // /map" case where no remount happens). After flying we drop a temporary
  // pulsing pin at the target so the user can see exactly where the alert
  // occurred — auto-dismisses after 30s.
  const flyToMarkerRef = useRef(null);
  const flyToTimeoutRef = useRef(null);
  useEffect(() => {
    const dropPin = (map, lat, lng) => {
      if (flyToMarkerRef.current) {
        flyToMarkerRef.current.remove();
        flyToMarkerRef.current = null;
      }
      if (flyToTimeoutRef.current) {
        clearTimeout(flyToTimeoutRef.current);
        flyToTimeoutRef.current = null;
      }
      // Inject pulse keyframes once.
      if (!document.getElementById('trackizy-flyto-pin-kf')) {
        const style = document.createElement('style');
        style.id = 'trackizy-flyto-pin-kf';
        style.textContent = '@keyframes trackizyFlytoPulse{0%{transform:scale(1);opacity:0.9}100%{transform:scale(2.4);opacity:0}}';
        document.head.appendChild(style);
      }
      // Build the pin with safe DOM methods (no innerHTML).
      const el = document.createElement('div');
      el.style.cssText = 'position:relative;width:24px;height:24px;pointer-events:none;';
      const ring = document.createElement('span');
      ring.style.cssText = 'position:absolute;inset:0;border-radius:9999px;background:rgba(59,130,246,0.35);animation:trackizyFlytoPulse 1.6s ease-out infinite;';
      const dot = document.createElement('span');
      dot.style.cssText = 'position:absolute;inset:6px;border-radius:9999px;background:#3b82f6;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);';
      el.appendChild(ring);
      el.appendChild(dot);
      const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([lng, lat])
        .addTo(map);
      flyToMarkerRef.current = marker;
      flyToTimeoutRef.current = setTimeout(() => {
        marker.remove();
        flyToMarkerRef.current = null;
        flyToTimeoutRef.current = null;
      }, 30000);
    };
    const flyToCoords = (lat, lng) => {
      const map = mapViewRef.current?.getMap?.();
      if (!map) return false;
      map.flyTo({ center: [lng, lat], zoom: Math.max(map.getZoom(), 14), duration: 800 });
      dropPin(map, lat, lng);
      return true;
    };
    // Mount handoff via sessionStorage. The map may not be ready immediately
    // (style.load is async), so retry briefly.
    try {
      const raw = sessionStorage.getItem('trackizy_livemap_fly_to');
      if (raw) {
        const { lat, lng } = JSON.parse(raw);
        sessionStorage.removeItem('trackizy_livemap_fly_to');
        let attempts = 0;
        const tryFly = () => {
          if (flyToCoords(lat, lng) || ++attempts > 20) return;
          setTimeout(tryFly, 100);
        };
        tryFly();
      }
    } catch { /* malformed — ignore */ }
    // Live event for when /map is already mounted.
    const handler = (e) => {
      if (e.detail?.lat != null && e.detail?.lng != null) {
        flyToCoords(e.detail.lat, e.detail.lng);
      }
    };
    window.addEventListener('livemap-fly-to', handler);
    return () => {
      window.removeEventListener('livemap-fly-to', handler);
      if (flyToMarkerRef.current) flyToMarkerRef.current.remove();
      if (flyToTimeoutRef.current) clearTimeout(flyToTimeoutRef.current);
    };
  }, []);

  // Right-click router. Vehicle/staff hits route to VehicleListPanel via a
  // window event because the existing context menus there own SendCommand /
  // Share / DeviceDrawer dialog state and lifting all of that to the page
  // would be invasive. Map/POI hits open menus owned here.
  const handleMapContextMenu = useCallback((evt) => {
    const { kind, x, y, lngLat, payload } = evt;
    if (kind === 'map') {
      setMapMenu({ kind: 'map', x, y, lngLat });
      return;
    }
    if (kind === 'poi') {
      const poi = poiList.find((p) => String(p.id) === String(payload?.poiId));
      if (!poi) return;
      setMapMenu({ kind: 'poi', x, y, poi });
      return;
    }
    if (kind === 'vehicle' && payload?.deviceId != null) {
      dispatch(staffActions.clearSelection());
      dispatch(devicesActions.selectId(payload.deviceId));
      window.dispatchEvent(new CustomEvent('livemap-vehicle-context-menu', {
        detail: { deviceId: payload.deviceId, x, y },
      }));
      return;
    }
    if (kind === 'staff' && payload?.userId) {
      dispatch(devicesActions.selectId(null));
      dispatch(staffActions.selectUser(payload.userId));
      window.dispatchEvent(new CustomEvent('livemap-staff-context-menu', {
        detail: { userId: payload.userId, x, y },
      }));
    }
  }, [dispatch, poiList]);

  const handleCopyCoords = useCallback(async (lngLat) => {
    await copyCoordsToClipboard({ lat: lngLat.lat, lng: lngLat.lng });
  }, []);

  const handleCenterHere = useCallback((lngLat) => {
    const map = mapViewRef.current?.getMap?.();
    if (!map) return;
    map.flyTo({ center: [lngLat.lng, lngLat.lat], duration: 600 });
  }, []);

  const handleAddPoiHere = useCallback((lngLat) => {
    // POIForm reads poi?.latitude/longitude into initial state. Passing only
    // those two fields keeps poi.id undefined → save path stays as "create".
    setEditPoi({ latitude: lngLat.lat, longitude: lngLat.lng });
  }, []);

  // Staff feature data flows directly from Redux into MapView's unified
  // cluster source — no setStaffData round-trip needed anymore. useStaffPositions
  // dispatches into state.staff.byUserId; MapView reads from there.

  const sideTelemetryOpen = telemetryPrefs.layout === 'side'
    && Boolean(sideSelectedVehicleId || sideSelectedStaffId)
    && !editPoi && !visitsPoi && !riskDrawerOpen;

  return (
    <div className="relative h-full overflow-hidden">
      {/* Map fills entire background */}
      <div className="absolute inset-0 z-0">
        <MapView
          ref={mapViewRef}
          trailData={trailsEnabled ? trailGeoJSON : null}
          trailMinZoom={trailSettings.minZoom}
          panelSide={panel.side}
          panelWidth={panel.width}
          extraEndInset={sideTelemetryOpen && panel.side === 'start' ? telemetryPrefs.sideWidth + 12 : 0}
          vehiclesEnabled={vehiclesEnabled}
          animatePositions={animateEnabled}
          suppressSelectionFlyTo={!!miniReplay}
          followSelection={followEnabled}
          onMapContextMenu={handleMapContextMenu}
          layerTogglesEnabled
          layersStatus={layersStatus}
        />
        {miniReplay && (
          <MiniReplayLayer
            mapRef={mapViewRef}
            positions={miniReplay.positions}
            progress={replayEngine.progress}
            currentPosition={replayEngine.currentPosition}
            fitRouteRef={fitRouteRef}
            follow={replayFollow}
          />
        )}
        {miniReplay && (
          <ReplayEventLayer
            mapRef={mapViewRef}
            events={replayEvents}
            onSelect={handleSeekToEvent}
          />
        )}
      </div>

      {/* Glass top bar floating over map */}
      <div
        className="absolute top-0 left-0 right-0 z-30 h-12 flex items-center justify-between px-4"
        style={{
          background: 'var(--glass-bg)',
          backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          borderBottom: '1px solid var(--glass-border)',
        }}
      >
        <div className="flex items-center gap-2.5">
          <h2 className="text-sm font-semibold text-slate-800">{t('nav.liveMap')}</h2>
          <span className="text-[10px] text-slate-600 px-2 py-0.5 rounded-full tabular-nums bg-white/40 border border-white/40 flex items-center gap-2">
            <span className="font-semibold">
              {vehicleCount + staffCount} {t('vehicleList.totalLabel', 'total')}
            </span>
            <span className="flex items-center gap-2 text-slate-500 font-normal">
              <span title={t('vehicleList.tipAllVehicles', 'All vehicles')} className="flex items-center gap-0.5">
                <Car size={11} className="text-emerald-600" /> {vehicleCount}
              </span>
              {peopleRowVisible && (
                <span title={t('vehicleList.staffSection', 'People')} className="flex items-center gap-0.5">
                  <Users size={11} className="text-blue-600" /> {staffCount}
                </span>
              )}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell variant="glass" />
          <button
            onClick={() => navigate('/help#6-live-map')}
            className="p-2 rounded-xl transition-all hover:bg-white/40"
            title={t('help.openForThisScreen', 'Help for this screen')}
            aria-label={t('help.openForThisScreen', 'Help for this screen')}
          >
            <HelpCircle size={16} className="text-slate-500" />
          </button>
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className={cn('p-2 rounded-xl transition-all hover:bg-white/40', langOpen && 'bg-white/40')}
            >
              <Globe size={16} className="text-slate-500" />
            </button>
            {langOpen && (
              <div
                className="absolute end-0 top-full mt-1.5 w-40 rounded-xl overflow-hidden shadow-lg z-50"
                style={{
                  background: 'var(--glass-bg)',
                  backdropFilter: 'blur(24px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(24px) saturate(180%)',
                  border: '1px solid var(--glass-border)',
                }}
              >
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { i18n.changeLanguage(lang.code); setLangOpen(false); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-white/40 transition-colors"
                  >
                    <span className="w-5 h-5 rounded-md bg-white/50 flex items-center justify-center text-[10px] font-bold text-slate-600">
                      {lang.flag}
                    </span>
                    <span className="flex-1 text-start text-slate-700 font-medium">{lang.label}</span>
                    {i18n.language === lang.code && <Check size={14} className="text-blue-500" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Glass vehicle list panel floats over map */}
      <div
        className="absolute top-12 bottom-0 z-10 transition-[inset] duration-200"
        style={{ [panel.side === 'start' ? 'insetInlineStart' : 'insetInlineEnd']: 0 }}
      >
        <VehicleListPanel width={panel.width} onDragStart={panel.onDragStart} side={panel.side} onStartReplay={handleStartReplay} statusFilter={statusFilter} onStatusFilterChange={setStatusFilter} groupFilter={groupFilter} onGroupFilterChange={setGroupFilter} peopleEnabled={staffLayerVisible} setPeopleEnabled={setStaffLayerVisible} vehiclesEnabled={vehiclesEnabled} setVehiclesEnabled={setVehiclesEnabled} slotBelowPeople={telemetryPrefs.layout === 'docked' ? <DockedTelemetrySlot t={t} /> : null} slotBelowPeopleHeight={telemetryPrefs.layout === 'docked' ? telemetryPrefs.dockHeight : null} bottomInset={telemetryPrefs.layout === 'floating' ? floatingPanelHeight : 0} onSlotHeightChange={telemetryPrefs.setDockHeight} />
      </div>

      {/* Panel controls — flip, trails, style switcher, saved views — next to panel edge */}
      <div
        className="absolute top-16 z-15 flex flex-col gap-1.5 transition-[inset] duration-200"
        style={{ [panel.side === 'start' ? 'insetInlineStart' : 'insetInlineEnd']: panel.width + 8 }}
      >
      <button
        onClick={panel.onFlip}
        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium text-white/80 hover:text-white transition-all"
        style={{
          background: 'rgba(15,23,42,0.75)',
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--glass-border)',
        }}
        title={t('map.flipPanel')}
      >
        <ArrowLeftRight size={12} />
        {t('map.flipPanel')}
      </button>
      <div className="relative">
        <button
          onClick={toggleTrails}
          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all ${
            trailsEnabled ? 'text-white' : 'text-white/80 hover:text-white'
          }`}
          style={{
            background: trailsEnabled ? 'rgba(16,185,129,0.85)' : 'rgba(15,23,42,0.75)',
            backdropFilter: 'blur(12px)',
            border: `1px solid ${trailsEnabled ? 'rgba(16,185,129,0.3)' : 'var(--glass-border)'}`,
          }}
          title={t('map.trails')}
        >
          <Activity size={12} />
          {t('map.trails')}
        </button>
        {trailsEnabled && (
          <button
            onClick={(e) => { e.stopPropagation(); setTrailSettingsOpen((p) => !p); }}
            className="absolute -bottom-1 -end-1 w-4 h-4 rounded-full flex items-center justify-center text-white bg-slate-700 border border-slate-500 hover:bg-slate-600 transition-colors"
            title={t('map.trailSettings', 'Trail Settings')}
          >
            <Settings2 size={8} />
          </button>
        )}
        {trailSettingsOpen && (
          <div
            className="absolute top-full mt-2 start-0 z-30 w-56 rounded-xl p-3 space-y-3 text-xs"
            style={{
              background: 'rgba(15,23,42,0.92)',
              backdropFilter: 'blur(20px)',
              border: '1px solid var(--glass-border)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            }}
          >
            <div>
              <div className="flex items-center justify-between text-white/60 mb-1">
                <span>{t('map.trailLength', 'Trail Length')}</span>
                <span className="text-white font-medium">{trailSettings.maxPoints} pts</span>
              </div>
              <input type="range" min="10" max="60" step="5" value={trailSettings.maxPoints}
                onChange={(e) => updateTrailSettings({ maxPoints: Number(e.target.value) })}
                className="w-full h-1 rounded-full appearance-none bg-white/20 accent-emerald-500" />
            </div>
            <div>
              <div className="flex items-center justify-between text-white/60 mb-1">
                <span>{t('map.trailDuration', 'Duration')}</span>
                <span className="text-white font-medium">{trailSettings.maxMinutes} min</span>
              </div>
              <input type="range" min="2" max="15" step="1" value={trailSettings.maxMinutes}
                onChange={(e) => updateTrailSettings({ maxMinutes: Number(e.target.value) })}
                className="w-full h-1 rounded-full appearance-none bg-white/20 accent-emerald-500" />
            </div>
            <div>
              <div className="flex items-center justify-between text-white/60 mb-1">
                <span>{t('map.trailMinZoom', 'Min Zoom')}</span>
                <span className="text-white font-medium">{trailSettings.minZoom}</span>
              </div>
              <input type="range" min="6" max="14" step="1" value={trailSettings.minZoom}
                onChange={(e) => updateTrailSettings({ minZoom: Number(e.target.value) })}
                className="w-full h-1 rounded-full appearance-none bg-white/20 accent-emerald-500" />
            </div>
          </div>
        )}
      </div>
      <button
        onClick={toggleAnimate}
        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all ${
          animateEnabled ? 'text-white' : 'text-white/80 hover:text-white'
        }`}
        style={{
          background: animateEnabled ? 'rgba(16,185,129,0.85)' : 'rgba(15,23,42,0.75)',
          backdropFilter: 'blur(12px)',
          border: `1px solid ${animateEnabled ? 'rgba(16,185,129,0.3)' : 'var(--glass-border)'}`,
        }}
        title={t('map.animateMarkers', 'Smooth marker animation between GPS fixes (1 fix behind real-time)')}
      >
        <Waypoints size={12} />
        {t('map.animate', 'Animate')}
      </button>
      <button
        onClick={toggleFollow}
        aria-pressed={followEnabled}
        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all ${
          followEnabled ? 'text-white' : 'text-white/80 hover:text-white'
        }`}
        style={{
          background: followEnabled ? 'rgba(16,185,129,0.85)' : 'rgba(15,23,42,0.75)',
          backdropFilter: 'blur(12px)',
          border: `1px solid ${followEnabled ? 'rgba(16,185,129,0.3)' : 'var(--glass-border)'}`,
        }}
        title={t('map.followSelectionHint', 'Keep the map centred on the selected vehicle as it moves. Off: the map goes to it once when you select it, then stays where you leave it')}
      >
        <LocateFixed size={12} />
        {t('map.followSelection', 'Follow')}
      </button>
      {features.snapToRoad && (
        <div className="relative">
          <button
            onClick={toggleSnap}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all ${
              snapEnabled ? 'text-white' : 'text-white/80 hover:text-white'
            }`}
            style={{
              background: snapEnabled ? 'rgba(59,130,246,0.85)' : 'rgba(15,23,42,0.75)',
              backdropFilter: 'blur(12px)',
              border: `1px solid ${snapEnabled ? 'rgba(59,130,246,0.3)' : 'var(--glass-border)'}`,
            }}
            title={t('map.snapToRoadHint', 'Align vehicle markers and trails to the road network using OSRM map matching')}
          >
            <Magnet size={12} />
            {t('map.snapToRoad', 'Snap')}
          </button>
          {snapEnabled && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setSnapSettingsOpen((p) => !p); }}
              className="absolute -bottom-1 -end-1 w-4 h-4 rounded-full flex items-center justify-center text-white bg-slate-700 border border-slate-500 hover:bg-slate-600 transition-colors"
              title={t('map.snapSettings', 'Snap settings')}
            >
              <Settings2 size={8} />
            </button>
          )}
          {snapSettingsOpen && (
            <div
              className="absolute top-full mt-2 end-0 z-30 w-64 rounded-xl p-3 space-y-2 text-xs"
              style={{
                background: 'rgba(15,23,42,0.92)',
                backdropFilter: 'blur(20px)',
                border: '1px solid var(--glass-border)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
              }}
            >
              <div>
                <div className="flex items-center justify-between text-white/60 mb-1">
                  <span>{t('map.snapThreshold', 'Snap threshold')}</span>
                  <span className="text-white font-medium">{snapThresholdM} m</span>
                </div>
                <input
                  type="range"
                  min={snapThresholdBounds.min}
                  max={snapThresholdBounds.max}
                  step={5}
                  value={snapThresholdM}
                  onChange={(e) => setSnapThresholdM(Number(e.target.value))}
                  className="w-full h-1 rounded-full appearance-none bg-white/20 accent-blue-500"
                />
                <p className="text-[10px] text-white/50 mt-1.5 leading-snug">
                  {t('map.snapThresholdHint', 'How far the marker can be from the snapped road before we let it show its raw GPS position instead. Lower = stricter (marker stays glued to the road); higher = lenient (marker snaps from further away).')}
                </p>
                <p className="text-[10px] text-white/60 mt-1.5">
                  {snapCounts.matched > 0 ? (
                    <>
                      {/* Numeric ratio isolated in <bdi> so the slash doesn't reorder in RTL */}
                      <bdi>{snapCounts.snapped} / {snapCounts.matched}</bdi>{' '}
                      {t('map.snapCount', 'vehicles snapped to road')}
                    </>
                  ) : (
                    t('map.snapCountEmpty', 'No vehicles being matched yet')
                  )}
                </p>
              </div>
              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  onClick={() => setSnapThresholdM(snapThresholdBounds.defaultValue)}
                  className="text-[10px] text-white/60 hover:text-white transition-colors"
                >
                  {t('map.resetDefault', 'Reset to {{n}} m', { n: snapThresholdBounds.defaultValue })}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      </div>

      {/* Telemetry strip — floating layout only (unchanged from pre-replay behavior) */}
      {telemetryPrefs.layout === 'floating' && (
        <div ref={floatingPanelRef} className="absolute bottom-0 left-0 right-0 z-20 flex flex-col">
          <TelemetryPanel
            mode="floating"
            replayPosition={replayPosition}
            onStartReplay={handleStartReplay}
          />
        </div>
      )}

      {/* Telemetry side drawer — glass panel on the map edge opposite the list (flips with it) */}
      {sideTelemetryOpen && (
        <div
          data-testid="telemetry-side-drawer"
          className="absolute top-12 bottom-0 z-20 transition-[inset] duration-200 shadow-2xl"
          style={{
            width: telemetryPrefs.sideWidth,
            background: 'var(--glass-bg)',
            backdropFilter: 'blur(40px) saturate(180%)',
            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
            borderInlineStart: panel.side === 'start' ? '1px solid var(--glass-border)' : 'none',
            borderInlineEnd: panel.side === 'end' ? '1px solid var(--glass-border)' : 'none',
            [panel.side === 'start' ? 'insetInlineEnd' : 'insetInlineStart']: 0,
          }}
        >
          <SideDrawerResizeHandle
            value={telemetryPrefs.sideWidth}
            onChange={telemetryPrefs.setSideWidth}
            drawerSide={panel.side === 'start' ? 'end' : 'start'}
            ariaLabel={t('telemetry.resizeDrawer', 'Resize telemetry drawer')}
          />
          <div className="h-full overflow-y-auto">
            <TelemetryPanel
              mode="side"
              replayPosition={replayPosition}
              onStartReplay={handleStartReplay}
            />
          </div>
        </div>
      )}

      {/* Floating trip player — portaled over the map, independent of telemetry layout */}
      {miniReplay && (
        <ReplayTripPlayer
          deviceName={miniReplay.deviceName}
          positions={miniReplay.positions}
          progress={replayEngine.progress}
          playing={replayEngine.playing}
          speed={replayEngine.speed}
          currentPosition={replayEngine.currentPosition}
          onToggle={replayEngine.toggle}
          onSeek={replayEngine.seek}
          onSpeedChange={replayEngine.setSpeed}
          onClose={handleCloseReplay}
          onFitRoute={() => fitRouteRef.current?.()}
          follow={replayFollow}
          onToggleFollow={toggleReplayFollow}
          events={replayEvents}
          activeId={activeEventId}
          onSeekToEvent={handleSeekToEvent}
        />
      )}

      {/* Date range popover */}
      {datePopover && (
        <div
          ref={datePopoverRef}
          className="absolute z-50 w-80 rounded-2xl p-4 space-y-3 shadow-xl border border-white/30 dark:border-slate-700/50 transition-[inset] duration-200"
          style={{
            background: 'var(--glass-bg)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            top: '100px',
            [panel.side === 'start' ? 'insetInlineStart' : 'insetInlineEnd']: panel.width + 8,
          }}
        >
          {/* Dark mode overlay */}
          <div className="hidden dark:block absolute inset-0 rounded-2xl pointer-events-none" style={{ background: 'rgba(15,23,42,0.88)', backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)' }} />

          <div className="relative z-10 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">{t('miniReplay.selectDateRange')}</h3>
            <button type="button" onClick={() => setDatePopover(null)} className="p-1 rounded-lg hover:bg-white/40 text-slate-400">
              <X size={14} />
            </button>
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 font-medium truncate">{datePopover.deviceName}</div>

          {/* Preset chips */}
          <div className="flex gap-1.5">
            {[
              { label: t('miniReplay.today'), from: () => dayjs().startOf('day'), to: () => dayjs().endOf('day') },
              { label: t('miniReplay.yesterday'), from: () => dayjs().subtract(1, 'day').startOf('day'), to: () => dayjs().subtract(1, 'day').endOf('day') },
              { label: t('miniReplay.last7Days'), from: () => dayjs().subtract(7, 'day').startOf('day'), to: () => dayjs().endOf('day') },
            ].map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => handlePreset(preset.from, preset.to)}
                className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 transition-colors"
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Date inputs */}
          <div className="space-y-2">
            <label className="block">
              <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">{t('miniReplay.from')}</span>
              <input
                type="datetime-local"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="mt-0.5 w-full px-3 py-1.5 rounded-lg text-xs bg-white/60 border border-slate-200/80 text-slate-700 dark:bg-slate-800/60 dark:border-slate-600 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">{t('miniReplay.to')}</span>
              <input
                type="datetime-local"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="mt-0.5 w-full px-3 py-1.5 rounded-lg text-xs bg-white/60 border border-slate-200/80 text-slate-700 dark:bg-slate-800/60 dark:border-slate-600 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
            </label>
          </div>

          {/* Error states */}
          {replayError === 'noPositions' && (
            <div className="text-xs text-amber-600 bg-amber-50/80 rounded-lg p-2">
              <p className="font-semibold">{t('miniReplay.noPositions')}</p>
              <p className="text-amber-500 mt-0.5">{t('miniReplay.noPositionsHint')}</p>
            </div>
          )}
          {replayError === 'errorLoading' && (
            <div className="text-xs text-red-600 bg-red-50/80 rounded-lg p-2">{t('miniReplay.errorLoading')}</div>
          )}

          {/* Submit button */}
          <button
            type="button"
            onClick={handleFetchReplay}
            disabled={replayLoading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors"
          >
            {replayLoading ? <Loader2 size={16} className="animate-spin" /> : t('miniReplay.startReplay')}
          </button>
          </div>
        </div>
      )}

      {/* Right-click menus — vehicle/staff variants live in VehicleListPanel
          and open via window-event bridge from handleMapContextMenu. */}
      {mapMenu?.kind === 'map' && (
        <MapContextMenu
          x={mapMenu.x}
          y={mapMenu.y}
          lngLat={mapMenu.lngLat}
          onClose={() => setMapMenu(null)}
          onAddPOI={handleAddPoiHere}
          onCopyCoords={handleCopyCoords}
          onCenterHere={handleCenterHere}
        />
      )}
      {mapMenu?.kind === 'poi' && (
        <POIContextMenu
          poi={mapMenu.poi}
          x={mapMenu.x}
          y={mapMenu.y}
          onClose={() => setMapMenu(null)}
          onViewVisits={(poi) => setVisitsPoi(poi)}
          onEdit={(poi) => setEditPoi(poi)}
          onCopyCoords={handleCopyCoords}
        />
      )}

      {/* POI add/edit drawer — glass panel docked opposite the asset panel. */}
      {editPoi && (
        <div
          className="absolute top-12 bottom-0 z-30 w-[360px] overflow-y-auto transition-[inset] duration-200 shadow-2xl"
          style={{
            background: 'var(--glass-bg)',
            backdropFilter: 'blur(40px) saturate(180%)',
            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
            borderInlineStart: panel.side === 'end' ? '1px solid var(--glass-border)' : 'none',
            borderInlineEnd: panel.side === 'start' ? '1px solid var(--glass-border)' : 'none',
            [panel.side === 'start' ? 'insetInlineEnd' : 'insetInlineStart']: 0,
          }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/30 dark:border-slate-700/50">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">
              {editPoi?.id ? t('pois.editPoi', 'Edit POI') : t('pois.newPoi', 'New POI')}
            </h3>
            <button
              type="button"
              onClick={() => setEditPoi(null)}
              className="p-1 rounded-lg hover:bg-white/40 text-slate-400"
            >
              <X size={14} />
            </button>
          </div>
          <POIForm
            poi={editPoi?.id ? editPoi : editPoi}
            onSaved={() => setEditPoi(null)}
            onCancel={() => setEditPoi(null)}
          />
        </div>
      )}

      {visitsPoi && (
        <VisitHistoryDrawer poi={visitsPoi} onClose={() => setVisitsPoi(null)} topOffset={48} />
      )}

      {/* Riskmize overlay — incidents layer sync, popup, drawer */}
      <RiskmizeMapOverlay mapViewRef={mapViewRef} panelSide={panel.side} />
      <AircraftOverlay mapViewRef={mapViewRef} premium={Boolean(layersStatus?.aircraft?.premium)} />
      <LiveuamapOverlay mapViewRef={mapViewRef} />
    </div>
  );
}
